<script setup lang="ts">
/**
 * How a table is drawn: the style of the library it follows, and what it
 * adjusts from it.
 *
 * A table names its style by id and keeps only its own adjustments, which are
 * its `look` (see `look.ts`, `tableStyle.ts`). The controls show the look in
 * force, the style's settings with the table's on top, and mark each setting
 * this table sets differently, with a way back to the style. Setting something
 * to what the style already says is not an adjustment and is not stored, so
 * putting a table back in step is setting it back.
 *
 * Writes the table's `styleId` and `look` and nothing else, apart from the one
 * older setting a choice of lines replaces: the table's own "rule between
 * every row", which the lines now say.
 */
import { useI18n } from 'vue-i18n'

import { computed, ref } from 'vue'

import { Select } from '@/components/ui'
import type { TableSection } from '@/domain/section'
import TableLookControls from '@/sections/table/TableLookControls.vue'
import TableStyleLibrary from '@/sections/table/TableStyleLibrary.vue'
import { hasOwnLook, type TableLook } from '@/sections/table/look'
import {
  adjusted,
  adjustedKeys,
  effectiveLook,
  NO_STYLE,
  styleOf,
} from '@/sections/table/tableStyle'
import { useTableStylesStore } from '@/stores/tableStyles'

const { t } = useI18n()
const library = useTableStylesStore()

const props = defineProps<{ section: TableSection }>()
const emit = defineEmits<{ change: [section: TableSection] }>()

const DEFAULT = 'default'
const NONE = 'none'

const style = computed(() => styleOf(props.section, library.styles))

/** What is in force, with the table's own "rule between every row" read as the lines it stands for. */
const shown = computed<TableLook>(() => {
  const look = effectiveLook(props.section, library.styles)
  return look.rules || !props.section.ruled ? look : { ...look, rules: 'horizontal' }
})

const marked = computed(() => adjustedKeys(props.section, library.styles))

/** The choice the list shows: the library's default while the table names none. */
const chosen = computed(() => {
  if (props.section.styleId === NO_STYLE) return NONE
  if (props.section.styleId === undefined) return library.defaultStyle ? DEFAULT : NONE
  return String(props.section.styleId)
})

function chooseStyle(value: string | undefined): void {
  const { styleId: _own, ...rest } = props.section
  if (value === DEFAULT || value === undefined) emit('change', rest)
  else if (value === NONE) emit('change', { ...rest, styleId: NO_STYLE })
  else emit('change', { ...rest, styleId: Number(value) })
}

function withLookOf(look: TableLook, extra: Partial<TableSection> = {}): TableSection {
  const { look: _dropped, ...rest } = props.section
  return Object.keys(look).length > 0 ? { ...rest, look, ...extra } : { ...rest, ...extra }
}

function set(key: keyof TableLook, value: TableLook[keyof TableLook] | undefined): void {
  const next = adjusted(props.section, library.styles, key, value as never)
  // The lines now say what the older switch said; it no longer adds its own.
  emit('change', withLookOf(next, key === 'rules' ? { ruled: false } : {}))
}

function revert(key: keyof TableLook): void {
  const { [key]: _gone, ...rest } = props.section.look ?? {}
  emit('change', withLookOf(rest))
}

function revertAll(): void {
  emit('change', withLookOf({}))
}

const showLibrary = ref(false)

function openLibrary(): void {
  showLibrary.value = true
}
</script>

<template>
  <div class="look">
    <div class="look__head">
      <h3 class="look__title">{{ t('inspector.table.style.title') }}</h3>
      <button
        v-if="marked.length > 0 || (!style && hasOwnLook(section.look))"
        type="button"
        class="b-btn look__reset"
        @click="revertAll"
      >
        {{ style ? t('inspector.table.style.revertAll') : t('inspector.table.look.reset') }}
      </button>
    </div>

    <div class="look__style">
      <Select
        size="sm"
        :model-value="chosen"
        :aria-label="t('inspector.table.style.title')"
        @update:model-value="chooseStyle"
      >
        <option v-if="library.defaultStyle" :value="DEFAULT">
          {{ t('inspector.table.style.byDefault', { name: library.defaultStyle.name }) }}
        </option>
        <option v-for="each in library.styles" :key="each.id" :value="String(each.id)">
          {{ each.name }}
        </option>
        <option :value="NONE">{{ t('inspector.table.style.none') }}</option>
      </Select>
      <button type="button" class="b-btn look__library" @click="openLibrary">
        {{ t('inspector.table.style.library') }}
      </button>
    </div>

    <p v-if="style && marked.length > 0" class="look__note">
      {{ t('inspector.table.style.adjusted', { count: marked.length }, marked.length) }}
    </p>
    <p
      v-else-if="section.styleId !== undefined && section.styleId !== 0 && !style"
      class="look__note"
    >
      {{ t('inspector.table.style.missing') }}
    </p>

    <TableLookControls :look="shown" :adjusted="marked" @set="set" @revert="revert" />

    <TableStyleLibrary v-if="showLibrary" @close="showLibrary = false" />
  </div>
</template>

<style scoped>
.look {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
  padding-top: var(--sp-6);
  border-top: 1px solid var(--hairline);
}

.look__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-4);
}

.look__title {
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink);
}

.look__reset {
  height: 26px;
  padding: 0 var(--sp-4);
  color: var(--accent);
}

.look__style {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
}

.look__library {
  align-self: flex-start;
  height: 26px;
  padding: 0 var(--sp-4);
  color: var(--accent);
}

.look__note {
  margin: 0;
  color: var(--ink-faint);
  font-size: var(--text-xs);
  line-height: 1.45;
}
</style>
